// Placement assist (IMPLEMENTATION §0.2 R14, GDD §3.3): a new machine faces the first adjacent acceptor of
// its output — machines first, then sinks (Crate, Gallery, Dock), then chutes; ties in N/E/S/W order. The
// check is by family and side only (not by current room), so it is stable while the neighbour fills up.
// A new drill faces the first adjacent deposit. With nothing to face, a building faces `DEFAULT_DIR`
// (south: toward the Gallery row of the reference cell, GDD §10).

import {
  defaultMode,
  isMachineType,
  isProcessorType,
  isSinkType,
  recipeById,
  slotFams,
} from '../bdefs.ts';
import { DIRS, opposite, stepCell } from '../core/grid.ts';
import type { Dir, Int } from '../core/types.ts';
import type { SimDefs } from '../defs.ts';
import { famDef, findBuilding } from '../entities.ts';
import type { BType, FamId } from '../gen/ids.ts';
import { type Occ, occAt } from '../occupancy.ts';
import type { Building, SimState } from '../state.ts';

export const DEFAULT_DIR: Dir = 'S';

/** The family a new building of `type` outputs (null: no output). Packers are assumed to pack ammo. */
export function outputFam(d: SimDefs, type: BType): FamId | null {
  switch (type) {
    case 'caster':
    case 'grinder':
    case 'smelter':
    case 'press': {
      const mode = defaultMode(d, type);
      for (const m of d.buildings[type].modes) {
        const id = m.recipes[0];
        if (m.id === mode && id !== undefined) return recipeById(d, id).out;
      }
      return null;
    }
    case 'loader':
    case 'gunsmith':
      return recipeById(d, d.buildings[type].recipe).out;
    case 'packer':
      return 'ammo';
    default:
      return null;
  }
}

/** Could `b` ever take `fam` arriving on `side` (family and side only)? */
export function familyAccepts(d: SimDefs, b: Building, fam: FamId, side: Dir): boolean {
  const kind = famDef(d, fam).kind;
  switch (b.type) {
    case 'chute':
      return side === b.inDir;
    case 'crate':
      return kind !== 'gun';
    case 'gallery':
      return fam === 'ammo';
    case 'packer':
      return side !== b.dir && kind !== 'gun';
    case 'deposit':
    case 'drill':
    case 'dock':
      return false;
    default:
      if (side === b.dir) return false;
      if (b.type === 'smelter' && fam === d.buildings.smelter.fuel) return true;
      return slotFams(d, b).includes(fam);
  }
}

function rank(t: BType): Int {
  if (isMachineType(t)) return 0;
  if (isSinkType(t)) return 1;
  if (t === 'chute') return 2;
  return 9;
}

/** Facing for a new building of `type` at (x, y). */
export function autoFacing(s: SimState, d: SimDefs, occ: Occ, type: BType, x: Int, y: Int): Dir {
  if (type === 'drill') {
    for (const dir of DIRS) {
      const n = stepCell(x, y, dir);
      const id = occAt(occ, n.x, n.y);
      const b = id > 0 ? findBuilding(s, id) : null;
      if (b !== null && b.type === 'deposit') return dir;
    }
    return DEFAULT_DIR;
  }
  const fam = outputFam(d, type);
  if (fam === null || !(isProcessorType(type) || type === 'packer')) return DEFAULT_DIR;
  let best: Dir = DEFAULT_DIR;
  let bestRank = 9;
  for (const dir of DIRS) {
    const n = stepCell(x, y, dir);
    const id = occAt(occ, n.x, n.y);
    const b = id > 0 ? findBuilding(s, id) : null;
    if (b === null) continue;
    const r = rank(b.type);
    if (r >= bestRank) continue;
    if (familyAccepts(d, b, fam, opposite(dir))) {
      best = dir;
      bestRank = r;
    }
  }
  return best;
}
